import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { auth } from '../firebase';
import {
  IDLE_BROADCAST_CHANNEL,
  IDLE_MOUSE_THROTTLE_MS,
  getIdleUnsavedWorkStatus,
  idleWarningSecondsRemaining,
  isIdleExemptPath,
  parseIdleActivityStorageEvent,
  publishIdleActivityTimestamp,
  resolveIdleTimeouts,
  shouldAcceptThrottledActivity,
  shouldForceIdleSignOut,
  shouldShowIdleWarning,
  subscribeAppActivity,
} from '../lib/idleSession';

const IdleSessionContext = createContext(null);

/**
 * Central idle-session watchdog for signed-in users.
 * Mount inside BrowserRouter (needs navigate/location) on the back-office shell.
 */
export function IdleSessionProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [signedIn, setSignedIn] = useState(Boolean(auth.currentUser));
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [unsavedLabel, setUnsavedLabel] = useState('');
  const [flushNote, setFlushNote] = useState('');

  const lastActivityRef = useRef(Date.now());
  const lastMouseRef = useRef(0);
  const warningStartedRef = useRef(0);
  const signingOutRef = useRef(false);
  const timeoutsRef = useRef(resolveIdleTimeouts());

  const resetActivity = useCallback((opts = {}) => {
    const now = Date.now();
    lastActivityRef.current = now;
    if (opts.publish !== false) {
      publishIdleActivityTimestamp(now);
    }
    if (warningStartedRef.current) {
      warningStartedRef.current = 0;
      setWarningOpen(false);
      setSecondsLeft(0);
      setUnsavedLabel('');
      setFlushNote('');
    }
  }, []);

  const staySignedIn = useCallback(() => {
    resetActivity({ publish: true });
  }, [resetActivity]);

  const forceSignOut = useCallback(async () => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    warningStartedRef.current = 0;
    setWarningOpen(false);
    try {
      await signOut(auth);
    } catch (err) {
      console.warn('IdleSession: signOut failed', err);
    } finally {
      signingOutRef.current = false;
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // Auth gate
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setSignedIn(Boolean(user));
      if (user) {
        timeoutsRef.current = resolveIdleTimeouts();
        resetActivity({ publish: true });
      } else {
        warningStartedRef.current = 0;
        setWarningOpen(false);
      }
    });
    return () => unsub();
  }, [resetActivity]);

  // Route changes count as activity
  useEffect(() => {
    if (!signedIn) return;
    if (isIdleExemptPath(location.pathname)) return;
    resetActivity({ publish: true });
  }, [location.pathname, signedIn, resetActivity]);

  // DOM activity + long-running job bus + multi-tab
  useEffect(() => {
    if (!signedIn) return undefined;
    if (isIdleExemptPath(location.pathname)) return undefined;

    const onActivity = () => {
      // While the warning is up, require an explicit Stay / Sign out (or
      // activity from another tab). Ambient mouse jiggle must not dismiss.
      if (warningStartedRef.current) return;
      resetActivity({ publish: true });
    };

    const onMouseMove = () => {
      const now = Date.now();
      if (
        !shouldAcceptThrottledActivity(
          now,
          lastMouseRef.current,
          IDLE_MOUSE_THROTTLE_MS,
        )
      ) {
        return;
      }
      lastMouseRef.current = now;
      onActivity();
    };

    const onKeyDown = () => {
      // All keydowns count (typing + arrows/PageDown for reading).
      onActivity();
    };

    window.addEventListener('pointerdown', onActivity, { passive: true });
    window.addEventListener('keydown', onKeyDown, { passive: true });
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('scroll', onActivity, {
      passive: true,
      capture: true,
    });
    window.addEventListener('touchstart', onActivity, { passive: true });

    const unsubBus = subscribeAppActivity(() => {
      // Long-running jobs may still bump activity during the warning window
      // so a generate that spans the idle boundary does not force logout.
      resetActivity({ publish: true });
    });

    const onStorage = (event) => {
      const ts = parseIdleActivityStorageEvent(event);
      if (ts == null) return;
      // Remote tab activity — reset without re-broadcasting.
      lastActivityRef.current = Math.max(lastActivityRef.current, ts);
      if (warningStartedRef.current) {
        warningStartedRef.current = 0;
        setWarningOpen(false);
        setSecondsLeft(0);
        setUnsavedLabel('');
        setFlushNote('');
      }
    };
    window.addEventListener('storage', onStorage);

    /** @type {BroadcastChannel|null} */
    let channel = null;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel(IDLE_BROADCAST_CHANNEL);
        channel.onmessage = (event) => {
          const at = Number(event?.data?.at);
          if (!Number.isFinite(at) || at <= 0) return;
          if (event?.data?.type !== 'activity') return;
          lastActivityRef.current = Math.max(lastActivityRef.current, at);
          if (warningStartedRef.current) {
            warningStartedRef.current = 0;
            setWarningOpen(false);
            setSecondsLeft(0);
            setUnsavedLabel('');
            setFlushNote('');
          }
        };
      }
    } catch {
      channel = null;
    }

    return () => {
      window.removeEventListener('pointerdown', onActivity);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('scroll', onActivity, true);
      window.removeEventListener('touchstart', onActivity);
      window.removeEventListener('storage', onStorage);
      unsubBus();
      try {
        channel?.close();
      } catch {
        /* ignore */
      }
    };
  }, [signedIn, location.pathname, resetActivity]);

  // Idle / warning tick (also while tab is hidden)
  useEffect(() => {
    if (!signedIn) return undefined;
    if (isIdleExemptPath(location.pathname)) return undefined;

    const tick = () => {
      if (signingOutRef.current) return;
      const { idleMs, warnMs } = timeoutsRef.current;
      const now = Date.now();

      if (warningStartedRef.current) {
        const left = idleWarningSecondsRemaining(
          warningStartedRef.current,
          now,
          warnMs,
        );
        setSecondsLeft(left);
        if (shouldForceIdleSignOut(warningStartedRef.current, now, warnMs)) {
          forceSignOut();
        }
        return;
      }

      if (shouldShowIdleWarning(now, lastActivityRef.current, idleMs)) {
        warningStartedRef.current = now;
        setWarningOpen(true);
        setSecondsLeft(idleWarningSecondsRemaining(now, now, warnMs));

        const status = getIdleUnsavedWorkStatus();
        if (status.dirty) {
          setUnsavedLabel(status.label || 'open work');
          if (status.flush) {
            Promise.resolve()
              .then(() => status.flush())
              .then(() => {
                setFlushNote('A recovery save was attempted.');
              })
              .catch(() => {
                setFlushNote(
                  'Could not auto-save — sign-out will discard unsaved changes.',
                );
              });
          } else {
            setFlushNote(
              'Unsaved changes will be lost if you are signed out (autosave not available yet).',
            );
          }
        } else {
          setUnsavedLabel('');
          setFlushNote('');
        }
      }
    };

    const id = window.setInterval(tick, 1000);
    tick();
    return () => window.clearInterval(id);
  }, [signedIn, location.pathname, forceSignOut]);

  const value = useMemo(
    () => ({
      resetActivity,
      staySignedIn,
      warningOpen,
      secondsLeft,
    }),
    [resetActivity, staySignedIn, warningOpen, secondsLeft],
  );

  return (
    <IdleSessionContext.Provider value={value}>
      {children}
      <Dialog
        open={warningOpen}
        disableEscapeKeyDown
        aria-labelledby="idle-session-warning-title"
      >
        <DialogTitle id="idle-session-warning-title">
          Still there?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            You&apos;ll be signed out in{' '}
            <strong>{secondsLeft}</strong> second
            {secondsLeft === 1 ? '' : 's'} due to inactivity.
          </Typography>
          {unsavedLabel ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              You have unsaved {unsavedLabel} changes.
              {flushNote ? ` ${flushNote}` : ''}
            </Alert>
          ) : null}
          <Typography variant="body2" color="text.secondary">
            Choose Stay signed in to continue your session.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={forceSignOut} color="inherit">
            Sign out now
          </Button>
          <Button onClick={staySignedIn} variant="contained" autoFocus>
            Stay signed in
          </Button>
        </DialogActions>
      </Dialog>
    </IdleSessionContext.Provider>
  );
}

export function useIdleSession() {
  return useContext(IdleSessionContext);
}
